import { resolveNodeByUrlKey } from '@skill-trees/shared';
import { TreeInteractor } from '../tree-domain/TreeInteractor';

export interface NodeSelectionState {
    isSkillPanelOpen: boolean;
    /** Incremented when a narrow URL-landing collapse may run the one-shot bounce hint (4hv.2). */
    narrowCollapsedBounceToken: number;
}

export type SelectionAction =
    | {
          type: 'selected';
          nodeId: string;
          shouldCenter: boolean;
          /** Narrow + user switched to another leaf: view should center while collapsed, then open the panel. */
          deferSkillPanelOpenForNarrowOrient?: boolean;
          /** When deferring, true if the panel was expanded and needs time to finish collapsing before centering. */
          narrowOrientPanelWasOpen?: boolean;
          /**
           * The panel was closed and should open only after the centering animation, so
           * its width transition and guide render don't drop frames from the pan.
           */
          openSkillPanelAfterCenter?: boolean;
          /**
           * First URL-driven landing: view should play the entry intro script (zoomed-out
           * overview → pan → zoom → select → dwell → panel). Selection and panel opening
           * are deferred to the script's selectNode / panel steps.
           */
          entryIntro?: boolean;
          /** Whether the entry intro may open the skill panel as its final step. */
          entryIntroOpensSkillPanel?: boolean;
      }
    | { type: 'deselected' }
    | { type: 'not_found' }
    | { type: 'no_change' }
    | { type: 'waiting' };

export type UrlDrivenSelectionLayout = {
    /** When true, URL/sync-driven opens keep the panel collapsed until the user selects a leaf explicitly (tree or panel link). */
    isNarrowViewport: boolean;
};

/** Optional policy for whether the skill panel may open for a selection (view mode + leaf without guide → false). */
export type SkillPanelSelectionPolicy = {
    openSkillPanelForSelection?: boolean;
};

export class NodeSelectionInteractor {
    private state: NodeSelectionState = { isSkillPanelOpen: false, narrowCollapsedBounceToken: 0 };
    private updateCallback?: (state: NodeSelectionState) => void;
    private hasUserToggledSkillPanel = false;
    /** Tree tap or panel link — enables panel open on narrow for URL-driven sync. */
    private explicitLeafSelectionForPanel = false;

    private narrowCollapsedBounceArmToken = 0;
    private narrowCollapsedBounceConsumed = false;
    private wasEligibleForNarrowBounce = false;
    private previousIsNarrowForBounce: boolean | null = null;
    /**
     * True while a “center, then open the panel” selection sequence is in progress —
     * the narrow collapse → center → open flow, or a wide centering whose panel open is
     * deferred until the pan finishes. Blocks resolveUrlSegment from opening the panel
     * early after navigateToNode syncs the URL.
     */
    private userOrientSequenceActive = false;
    /** The entry intro plays at most once per mount, on the first URL-driven landing. */
    private entryIntroPlayed = false;
    /**
     * True while the entry intro script drives selection and the panel. Blocks URL re-syncs
     * (the script's own selectNode step re-triggers path sync) from selecting or opening early.
     */
    private entryIntroSequenceActive = false;
    /**
     * True between a user selection and the URL catching up to it. React Router commits the
     * follow-up navigation in a transition after the selection's state updates, so path sync
     * can fire once more with the previous leaf's segment; that stale segment must not
     * re-select the old leaf (which flashes its panel open before the real segment arrives).
     */
    private pendingUserSelectionUrlSync = false;

    constructor(private readonly treeInteractor: TreeInteractor) {}

    getState(): NodeSelectionState {
        return { ...this.state };
    }

    setUpdateCallback(callback: (state: NodeSelectionState) => void): void {
        this.updateCallback = callback;
    }

    private updateState(updates: Partial<NodeSelectionState>): void {
        if (updates.isSkillPanelOpen === true) {
            this.narrowCollapsedBounceConsumed = true;
            this.wasEligibleForNarrowBounce = false;
        }
        this.state = { ...this.state, ...updates };
        this.updateCallback?.(this.getState());
    }

    /** View calls this after the bounce hint animation (or immediately if reduced-motion). */
    markNarrowCollapsedBounceAnimationDone(): void {
        this.narrowCollapsedBounceConsumed = true;
        this.wasEligibleForNarrowBounce = false;
    }

    private applyNarrowBouncePolicyAfterUrlSync(layout?: UrlDrivenSelectionLayout): void {
        const isNarrow = layout?.isNarrowViewport === true;
        if (this.previousIsNarrowForBounce === true && !isNarrow) {
            this.narrowCollapsedBounceConsumed = true;
            this.wasEligibleForNarrowBounce = false;
        }
        this.previousIsNarrowForBounce = isNarrow;

        const eligible =
            isNarrow &&
            !this.state.isSkillPanelOpen &&
            this.treeInteractor.getSelectedNodeId() !== null &&
            !this.explicitLeafSelectionForPanel;

        if (eligible && !this.narrowCollapsedBounceConsumed) {
            if (!this.wasEligibleForNarrowBounce) {
                this.wasEligibleForNarrowBounce = true;
                this.narrowCollapsedBounceArmToken += 1;
                this.updateState({ narrowCollapsedBounceToken: this.narrowCollapsedBounceArmToken });
            }
        } else {
            this.wasEligibleForNarrowBounce = false;
        }
    }

    private returnWithUrlSyncApply(action: SelectionAction, layout?: UrlDrivenSelectionLayout): SelectionAction {
        this.applyNarrowBouncePolicyAfterUrlSync(layout);
        return action;
    }

    selectNode(
        nodeId: string,
        layout?: UrlDrivenSelectionLayout,
        policy?: SkillPanelSelectionPolicy,
    ): SelectionAction {
        this.entryIntroSequenceActive = false;
        this.explicitLeafSelectionForPanel = true;
        this.pendingUserSelectionUrlSync = true;
        const prevId = this.treeInteractor.getSelectedNodeId();
        const isNarrow = layout?.isNarrowViewport === true;
        const openPanel = policy?.openSkillPanelForSelection !== false;
        const switchingLeavesOnNarrow = isNarrow && prevId !== null && prevId !== nodeId;
        const useNarrowOrient = openPanel && switchingLeavesOnNarrow;

        this.treeInteractor.setSelectedNode(nodeId);

        if (useNarrowOrient) {
            const panelWasOpen = this.state.isSkillPanelOpen;
            this.userOrientSequenceActive = true;
            this.updateState({ isSkillPanelOpen: false });
            this.applyNarrowBouncePolicyAfterUrlSync(layout);
            return {
                type: 'selected',
                nodeId,
                shouldCenter: true,
                deferSkillPanelOpenForNarrowOrient: true,
                narrowOrientPanelWasOpen: panelWasOpen,
            };
        }

        this.userOrientSequenceActive = false;
        this.updateState({ isSkillPanelOpen: openPanel });
        this.applyNarrowBouncePolicyAfterUrlSync(layout);
        return {
            type: 'selected',
            nodeId,
            shouldCenter: !openPanel && switchingLeavesOnNarrow,
        };
    }

    /** Animation-script-driven panel control (AnimationRobot stage). Ends any orient or entry intro sequence. */
    setSkillPanelOpenForAnimation(open: boolean, layout?: UrlDrivenSelectionLayout): void {
        this.userOrientSequenceActive = false;
        this.entryIntroSequenceActive = false;
        this.updateState({ isSkillPanelOpen: open });
        this.applyNarrowBouncePolicyAfterUrlSync(layout);
    }

    /** Completes the narrow “collapse → center → open” sequence after centering animation. */
    openSkillPanelForUserAfterNarrowOrient(layout?: UrlDrivenSelectionLayout): void {
        this.setSkillPanelOpenForAnimation(true, layout);
    }

    /**
     * A selection script died before its final steps — a user gesture interrupted one
     * of its viewport animations, or a step could not run (unpositioned node,
     * degenerate view area). Releases the URL-sync guards the script owned and applies
     * its deferred selection so URL and selection still agree; the panel is left as-is,
     * since the dropped choreography was only cosmetic.
     */
    abandonScriptSequence(nodeId?: string): void {
        this.userOrientSequenceActive = false;
        this.entryIntroSequenceActive = false;
        if (nodeId !== undefined && this.treeInteractor.getSelectedNodeId() !== nodeId) {
            this.treeInteractor.setSelectedNode(nodeId);
        }
    }

    selectNodeWithCentering(
        nodeId: string,
        options?: { interaction?: 'url' | 'user'; openSkillPanelForSelection?: boolean },
        layout?: UrlDrivenSelectionLayout,
    ): SelectionAction {
        const interaction = options?.interaction ?? 'user';
        const openSelection = options?.openSkillPanelForSelection !== false;
        this.entryIntroSequenceActive = false;
        if (interaction === 'url') {
            this.userOrientSequenceActive = false;
        }
        const isNarrow = layout?.isNarrowViewport === true;
        const prevId = this.treeInteractor.getSelectedNodeId();

        if (interaction === 'url' && prevId !== nodeId) {
            this.narrowCollapsedBounceConsumed = false;
            this.wasEligibleForNarrowBounce = false;
        }
        if (interaction === 'user') {
            this.explicitLeafSelectionForPanel = true;
            this.pendingUserSelectionUrlSync = true;
        }

        const useNarrowOrient =
            openSelection &&
            interaction === 'user' &&
            isNarrow &&
            prevId !== null &&
            prevId !== nodeId;

        this.treeInteractor.setSelectedNode(nodeId);

        if (useNarrowOrient) {
            const panelWasOpen = this.state.isSkillPanelOpen;
            this.userOrientSequenceActive = true;
            this.updateState({ isSkillPanelOpen: false });
            this.applyNarrowBouncePolicyAfterUrlSync(layout);
            return {
                type: 'selected',
                nodeId,
                shouldCenter: true,
                deferSkillPanelOpenForNarrowOrient: true,
                narrowOrientPanelWasOpen: panelWasOpen,
            };
        }

        const openPanel =
            openSelection &&
            (interaction === 'user' || !isNarrow || this.explicitLeafSelectionForPanel);

        if (openPanel && !this.state.isSkillPanelOpen) {
            // Open the panel only after the centering animation: its width transition and
            // guide render are heavy enough to drop frames from the pan.
            this.userOrientSequenceActive = true;
            this.applyNarrowBouncePolicyAfterUrlSync(layout);
            return { type: 'selected', nodeId, shouldCenter: true, openSkillPanelAfterCenter: true };
        }

        this.userOrientSequenceActive = false;
        this.updateState({ isSkillPanelOpen: openPanel });
        this.applyNarrowBouncePolicyAfterUrlSync(layout);
        return { type: 'selected', nodeId, shouldCenter: true };
    }

    deselectNode(layout?: UrlDrivenSelectionLayout): SelectionAction {
        this.userOrientSequenceActive = false;
        this.entryIntroSequenceActive = false;
        this.pendingUserSelectionUrlSync = false;
        this.treeInteractor.setSelectedNode(null);
        this.updateState({ isSkillPanelOpen: false });
        this.applyNarrowBouncePolicyAfterUrlSync(layout);
        return { type: 'deselected' };
    }

    toggleSkillPanel(layout?: UrlDrivenSelectionLayout): void {
        this.hasUserToggledSkillPanel = true;
        // The user took control of the panel; pending animation sequences must not fight them.
        this.userOrientSequenceActive = false;
        this.entryIntroSequenceActive = false;
        this.updateState({ isSkillPanelOpen: !this.state.isSkillPanelOpen });
        this.applyNarrowBouncePolicyAfterUrlSync(layout);
    }

    /**
     * Resolve a URL path segment to a node selection. Returns an action
     * describing what happened so the view can trigger navigation or centering.
     */
    private urlSyncMayAutoOpenPanel(
        layout?: UrlDrivenSelectionLayout,
        openSkillPanelForSelection: boolean = true,
    ): boolean {
        if (!openSkillPanelForSelection) {
            return false;
        }
        const isNarrow = layout?.isNarrowViewport === true;
        return !isNarrow || this.explicitLeafSelectionForPanel;
    }

    resolveUrlSegment(
        segment: string | null,
        nodes: readonly { id: string }[],
        layout?: UrlDrivenSelectionLayout,
        policy?: SkillPanelSelectionPolicy,
    ): SelectionAction {
        const openSelection = policy?.openSkillPanelForSelection !== false;
        // A running script (narrow orient, deferred panel open, or entry intro) owns
        // selection and the panel; URL syncs must not select or open anything under it.
        const scriptSequenceActive = this.userOrientSequenceActive || this.entryIntroSequenceActive;

        if (!segment) {
            if (
                !scriptSequenceActive &&
                this.treeInteractor.getSelectedNodeId() !== null &&
                !this.state.isSkillPanelOpen &&
                !this.hasUserToggledSkillPanel &&
                this.urlSyncMayAutoOpenPanel(layout, openSelection)
            ) {
                this.updateState({ isSkillPanelOpen: true });
            }
            return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
        }

        if (nodes.length === 0) return this.returnWithUrlSyncApply({ type: 'waiting' }, layout);

        const node = resolveNodeByUrlKey(nodes, segment);
        if (!node) {
            // An unknown segment while a script runs is transient churn, not a broken URL.
            return this.returnWithUrlSyncApply(
                { type: scriptSequenceActive ? 'no_change' : 'not_found' },
                layout,
            );
        }

        // A URL landing before any explicit user selection gets the entry intro tour,
        // both for a link to a leaf and for a tree loaded with a default selection.
        const entryIntroEligible = !this.entryIntroPlayed && !this.explicitLeafSelectionForPanel;

        if (this.treeInteractor.getSelectedNodeId() === node.id) {
            // The URL caught up with the selection — record it even while a script still
            // drives the panel, so back/forward navigation isn't blocked afterwards.
            this.pendingUserSelectionUrlSync = false;
            if (scriptSequenceActive) {
                return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
            }
            if (entryIntroEligible && !this.state.isSkillPanelOpen && !this.hasUserToggledSkillPanel) {
                return this.beginEntryIntro(node.id, layout, openSelection);
            }
            if (
                !this.state.isSkillPanelOpen &&
                !this.hasUserToggledSkillPanel &&
                this.urlSyncMayAutoOpenPanel(layout, openSelection)
            ) {
                this.updateState({ isSkillPanelOpen: true });
            }
            return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
        }

        if (scriptSequenceActive || this.pendingUserSelectionUrlSync) {
            // Stale segment: a script sequence or the user's selection navigation is in flight.
            return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
        }

        if (entryIntroEligible) {
            return this.beginEntryIntro(node.id, layout, openSelection);
        }

        return this.selectNodeWithCentering(
            node.id,
            { interaction: 'url', openSkillPanelForSelection: openSelection },
            layout,
        );
    }

    /**
     * Starts the one-shot entry intro: selection and panel opening are deferred to the
     * view's animation script (its selectNode / panel steps call back into the
     * interactors), so the URL-sync guard stays up until the script finishes or a user
     * selection takes over.
     */
    private beginEntryIntro(
        nodeId: string,
        layout?: UrlDrivenSelectionLayout,
        openSelection: boolean = true,
    ): SelectionAction {
        this.entryIntroPlayed = true;
        this.entryIntroSequenceActive = true;
        if (this.treeInteractor.getSelectedNodeId() !== nodeId) {
            // Fresh landing on a new node re-arms the one-shot narrow bounce hint,
            // matching what a direct URL-driven selection did.
            this.narrowCollapsedBounceConsumed = false;
            this.wasEligibleForNarrowBounce = false;
        }
        return this.returnWithUrlSyncApply(
            {
                type: 'selected',
                nodeId,
                shouldCenter: true,
                entryIntro: true,
                // The intro ends by revealing the guide even on narrow viewports — it
                // exists to teach new (especially mobile) users that leaves have guides.
                // Only a guideless leaf keeps the panel closed.
                entryIntroOpensSkillPanel: openSelection,
            },
            layout,
        );
    }

    /**
     * Process a legacy #hash value (edit mode). Returns an action so the
     * view can center and clear the hash.
     */
    processHash(
        hash: string,
        nodes: readonly { id: string }[],
        layout?: UrlDrivenSelectionLayout,
    ): SelectionAction {
        if (this.userOrientSequenceActive || this.entryIntroSequenceActive) {
            return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
        }
        const segment = hash.startsWith('#') ? hash.substring(1) : hash;
        if (!segment) return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);

        const node = resolveNodeByUrlKey(nodes, segment);
        if (!node) return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
        if (this.treeInteractor.getSelectedNodeId() === node.id) {
            return this.returnWithUrlSyncApply({ type: 'no_change' }, layout);
        }

        return this.selectNodeWithCentering(node.id, { interaction: 'url' }, layout);
    }
}
